import React, { useCallback, useEffect, useState, useMemo, useContext } from "react";
import { apiRequest } from "src/async/apiUtils";
import { toast } from "react-toastify";
import ConnectorList from "./ConnectorList";
import Breadcrumb from "./Breadcrumbs";
import { LinkIcon } from "@heroicons/react/24/outline";
import Search from "src/components/form/Search";
import { WorkspaceContext } from "src/context/workspace";

export const Settings = () => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState({ connectors: false });
  const [selectedConnector, setSelectedConnector] = useState(null);
  const [embedUrl, setEmbedUrl] = useState(null);
  const [loadingConnector, setLoadingConnector] = useState(null);
  const [keyword, setKeyword] = useState("");
  const { details: workspaceDetails } = useContext(WorkspaceContext);

  const fetchConnectors = useCallback(async () => {
    setLoading((prev) => ({ ...prev, connectors: true }));
    try {
      const res = await apiRequest("get", `portal/workspaces/:workspace_id/settings/workato/connectors`, { params: { workspace_id: workspaceDetails?._id } });
      setConnectors(res.data?.data || []);
    } catch {
    } finally {
      setLoading((prev) => ({ ...prev, connectors: false }));
    }
  }, [workspaceDetails?._id]);

  const handleConnectorSelect = async (connector) => {
    try {
      setLoadingConnector(connector.name);
      const body = { provider: connector.name };
      const params = { connection_id: connector?.id, workspace_id: workspaceDetails?._id };
      const url = connector?.id ? `portal/workspaces/:workspace_id/settings/workato/connections/:connection_id/jwt` : `portal/workspaces/:workspace_id/settings/workato/connections`;

      const { data } = await apiRequest("post", url, { body, params });

      if (data?.data?.embedUrl) {
        setEmbedUrl(data.data.embedUrl);
        setSelectedConnector(connector);
      } else {
        toast.error("Embed URL not received");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to load connector");
    } finally {
      setLoadingConnector(null);
    }
  };

  const handleShowConnectors = () => {
    setSelectedConnector(null);
    setEmbedUrl(null);
    fetchConnectors();
  };

  useEffect(() => {
    fetchConnectors();
  }, [fetchConnectors]);

  const filteredConnectors = useMemo(() => {
    if (!keyword.trim()) return connectors;
    const lower = keyword.toLowerCase();
    return connectors.filter((c) => c.title?.toLowerCase().includes(lower) || c.name?.toLowerCase().includes(lower));
  }, [keyword, connectors]);

  return (
    <div className="mx-auto w-full p-2">
      <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <Breadcrumb items={[{ label: "Connectors", icon: LinkIcon, onClick: handleShowConnectors }, selectedConnector ? { label: selectedConnector.title, active: true } : null].filter(Boolean)} />
        </div>

        {!selectedConnector && (
          <div className="relative w-full sm:w-64">
            <Search
              value={keyword}
              placeholder="Search services..."
              onChange={(e) => setKeyword(e.target.value)}
              className="rounded-xl border-gray-200 focus:ring-highlightColor"
            />
          </div>
        )}
      </div>

      {embedUrl ? (
        <div className="overflow-hidden rounded-xl border bg-white">
          <div className="overflow-hidden rounded-lg border-[12px] border-gray-100">
            <iframe
              src={embedUrl}
              className="h-[80vh] w-full"
              title="Workato"
            />
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <ConnectorList
            connectors={filteredConnectors}
            connectorsLoaded={!loading.connectors}
            onSelect={handleConnectorSelect}
            loadingConnector={loadingConnector}
          />
        </div>
      )}
    </div>
  );
};
